import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import moment from 'moment-timezone';
import { Moment } from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConditionsComponent } from '../../../components/conditions/conditions.component';
import { TravelTime } from '../../../models/flightModel';
import { Booking, Customer, OrderType, Shuttle, INIT_DATA } from '../../../models/orderModel';
import { BookingConfig, BookingConfigType, ShuttleConfig, BOOKING_CONFIG_QUALITY, BOOKING_CONFIG_SCANDIC, BOOKING_CONFIG_MABI, BOOKING_CONFIG_ARLANDAXPOAPARTMENTS, BOOKING_CONFIG_APART, BOOKING_CONFIG_AIDEN, filterTerminalsByDate } from '../../../models/BookingModel';
import { Person, PERSONS } from '../../../models/persons';
import { PlannedShuttleBuss } from '../../../models/ShuttleBookingModel';
import { DynamicScriptLoaderService } from '../../../services/dynamicScriptLoader.service';
import { CartService } from '../../../services/cart.service';
import { ShuttelbussService } from '../../../services/shuttlebuss.service';
import { ArrivalDepartureService } from '../../../services/arrivaldeparture.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { PaymentBottomsheetComponent } from '../payment-bottomsheet/payment-bottomsheet.component';
import { Router } from '@angular/router';
import { MyConfigService } from '../../../services/my-config.service';
import { findIana } from 'windows-iana';

@Component({
  selector: 'app-booking-service',
  templateUrl: './booking-service.component.html',
  styleUrls: ['./booking-service.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BookingServiceComponent implements OnInit, OnDestroy {
  @ViewChild('selectDepartureChild') selectDepartureChild;
  @ViewChild('selectArrivalChild') selectArrivalChild;

  get departureTerminals(): any[] {
    const date = this.bookingForm?.get('flightDepartureDate')?.value || this.minDate;
    return filterTerminalsByDate(this.bookingConfigShuttleDeparture?.terminals || [], date);
  }

  get arrivalTerminals(): any[] {
    const date = this.bookingForm?.get('flightArrivalDate')?.value || this.minDate;
    return filterTerminalsByDate(this.bookingConfigShuttleArrival?.terminals || [], date);
  }

  // static qualityXpoBookingNumberValidator(control: AbstractControl): ValidationErrors | null {
  //   if (!control.value) {
  //     return null;
  //   }

  //   const pattern = /^SE007S\d{7}$/;
  //   if (!pattern.test(control.value)) {
  //     return { qualityXpoBookingNumber: true };
  //   }

  //   return null;
  // }

  organizationId: string;
  isLoading = false;
  persons: Person[] = PERSONS;
  bookingForm: UntypedFormGroup;
  bundle: Booking = INIT_DATA;
  bookingConfig: BookingConfig;
  bookingConfigType: BookingConfigType;
  isBookingNrRequired = false;
  isCarIdRequired = true;
  isBusOverbooked = false;
  useFlightForArrival = false;
  minDate = new Date();

  bookingConfigQuality: BookingConfig = BOOKING_CONFIG_QUALITY;
  bookingConfigScandic: BookingConfig = BOOKING_CONFIG_SCANDIC;
  bookingConfigMabi: BookingConfig = BOOKING_CONFIG_MABI;
  bookingConfigArlandaXPOApartments: BookingConfig = BOOKING_CONFIG_ARLANDAXPOAPARTMENTS;
  bookingConfigAppart: BookingConfig = BOOKING_CONFIG_APART;
  bookingConfigAiden: BookingConfig = BOOKING_CONFIG_AIDEN;

  bookingConfigShuttleArrival: ShuttleConfig;
  bookingConfigShuttleDeparture: ShuttleConfig;
  plannedShuttleBussesDeparture: PlannedShuttleBuss[] = [];
  plannedShuttleBussesArrival: PlannedShuttleBuss[] = [];

  showDeparture = false;
  showArrival = false;
  showHandbaggage = true;

  arriveAtParking60Min: string;
  arriveAtParking75Min: string;
  arriveAtParking90Min: string;
  arriveAtParking105Min: string;
  arriveAtParking120Min: string;
  arriveAtParking135Min: string;
  arriveAtParking150Min: string;
  arriveAtParking165Min: string;
  arriveAtParking180Min: string;

  componentDestroyed$: Subject<boolean> = new Subject();

  departureTravelTime: TravelTime;
  arrivalTravelTime: TravelTime;
  arrivalBookingError: string = null;

  showBookingForm: boolean = true;
  showQualityDisabled: boolean = false;
  showArlandaXPOAppartmentsDisabled: boolean = false;

  arrivalDateError: string = null;
  departureDateError: string = null;

  get formatter() {
    return Intl.DateTimeFormat("sv-SE", {
      timeZone: this.getOrgTimeZone(),
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
  }

  constructor(
    public shuttleBussService: ShuttelbussService,
    public arrivalDepartureService: ArrivalDepartureService,
    public snackBar: SnackbarService,
    public snackbar: SnackbarService,
    private route: ActivatedRoute,
    public data: DynamicScriptLoaderService,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    public translate: TranslocoService,
    private cartService: CartService,
    private router: Router,
    private myConfigService: MyConfigService
  ) { }

  ngOnInit() {
    this.getOrganizationidId();
  }

  getOrganizationidId() {
    this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(params => {
      this.organizationId = params['organizationId'];
      if (params['organizationId'].toUpperCase() == 'AC589E4E-5E38-4555-9BBF-AD4B1E2AD0DA') {
        this.showBookingForm = false;
        this.showArlandaXPOAppartmentsDisabled = true;
      }
      this.getOrganizationConfig(this.organizationId);
    });
  }

  setDepartureFlight(flight: TravelTime) {
    this.departureTravelTime = flight;

    this.arriveAtParking60Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(60, 'm').format();
    this.arriveAtParking75Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(75, 'm').format();
    this.arriveAtParking90Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(90, 'm').format();
    this.arriveAtParking105Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(105, 'm').format();
    this.arriveAtParking120Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(120, 'm').format();
    this.arriveAtParking135Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(135, 'm').format();
    this.arriveAtParking150Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(150, 'm').format();
    this.arriveAtParking165Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(165, 'm').format();
    this.arriveAtParking180Min = moment(this.departureTravelTime.flight.departureTime.scheduled).subtract(180, 'm').format();
  }

  ///Formats a local date with offset to a string witout the offset data
  formatDate(sourceDate: Date): string {
    if (!sourceDate || isNaN(sourceDate.getTime())) {
      return '';
    }
    var twoDigitMonth = ((sourceDate.getMonth() + 1) >= 10) ? (sourceDate.getMonth() + 1) : '0' + (sourceDate.getMonth() + 1);
    var twoDigitDate = ((sourceDate.getDate()) >= 10) ? (sourceDate.getDate()) : '0' + (sourceDate.getDate());
    var twoDigitHour = ((sourceDate.getHours()) >= 10) ? (sourceDate.getHours()) : '0' + (sourceDate.getHours());
    var twoDigitMinute = ((sourceDate.getMinutes()) >= 10) ? (sourceDate.getMinutes()) : '0' + (sourceDate.getMinutes());
    var returnDate = sourceDate.getFullYear() + "-" + twoDigitMonth + "-" + twoDigitDate + 'T' + twoDigitHour + ':' + twoDigitMinute + ':00';
    return returnDate;
  }

  setArrivalFlight(flight: TravelTime) {
    this.arrivalBookingError = null;
    this.arrivalTravelTime = flight;

    if (this.bookingConfigShuttleArrival?.type == 'ARRIVALFLIGHTANDTIMETABLE') {
      let offset = this.bookingConfigShuttleArrival.shuttleDateOffsetMinutes;
      if (this.bookingForm.controls['onlyHandBaggage'].value == 1) {
        offset = this.bookingConfigShuttleArrival.shuttleDateOffsetMinutesNoBag;
      }

      const scheduled = this.arrivalTravelTime?.flight?.arrivalTime?.scheduled;
      if (scheduled) {
        const schedDate = new Date(scheduled);
        if (!isNaN(schedDate.getTime())) {
          var offsetDateStart = new Date(schedDate.getTime() + offset * 60000);
          var offsetDateEnd = new Date(schedDate.getTime() + offset * 60000 + 3600000);
          this.getPlannedOrderShuttleBusTypeArrival(this.formatDate(offsetDateStart), this.formatDate(offsetDateEnd));
        }
      }
    }
  }

  intitializeForm() {
    if (this.bookingConfigShuttleArrival && this.bookingConfigShuttleArrival.type == 'ARRIVALTIMETABLE') {
      this.showHandbaggage = false;
    }
    else {
      if (this.showDeparture == true) {
        this.showHandbaggage = false;
      }
      if (this.showArrival == true) {
        this.showHandbaggage = true;
      }
    }

    this.isBookingNrRequired = false;
    this.isCarIdRequired = this.bookingConfigType?.carIdRequired || false;

    if (this.bookingConfigType?.bookingNrArrivalRequired && this.showArrival) {
      this.isBookingNrRequired = true;
    }

    if (this.bookingConfigType?.bookingNrDepartureRequired && this.showDeparture) {
      this.isBookingNrRequired = true;
    }

    if (!this.bookingForm) {
      this.bookingForm = this.fb.group({
        flightDepartureDate: [''],
        routeDeparture: [''],
        terminalDeparture: [''],
        flightArrivalDate: [''],
        routeArrival: [''],
        terminalArrival: [''],
        firstname: ['', Validators.required],
        lastname: ['', Validators.required],
        email: ['', [Validators.email, Validators.required]],
        cellular: ['', Validators.required],
        bookingnr: [''],
        persons: ['', Validators.required],
        onlyHandBaggage: [0],
        comment: [''],
        consent: ['', Validators.required],
        carid: [''],
        shuttleDepartureTime: [''],
      });

      // reset departure/arrival inputs when number of persons changes
      this.bookingForm.controls['persons'].valueChanges
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(() => {
          this.isBusOverbooked = false;
          this.resetDepartureArrivalFields();
          this.checkPersonsOnRoutes();
        });
    }

    if (this.isCarIdRequired) {
      this.bookingForm.controls["carid"].setValidators([Validators.required, Validators.minLength(4), Validators.maxLength(9), Validators.pattern(/^[A-Z0-9]{4,9}$/)]);
    } else {
      this.bookingForm.controls["carid"].clearValidators();
    }
    this.bookingForm.controls["carid"].updateValueAndValidity();

    if (this.isBookingNrRequired && this.organizationId?.toUpperCase() !== 'D6B9D8C6-8FB5-41A5-AC2E-B3FC007A8251') {
      const validators = [Validators.required];
      this.bookingForm.controls["bookingnr"].setValidators(validators);
    } else {
      this.bookingForm.controls["bookingnr"].clearValidators();
    }
    this.bookingForm.controls["bookingnr"].updateValueAndValidity();
  }

  public openConsent() {
    const dialogRef = this.dialog.open(ConditionsComponent, {
      disableClose: true,
      data: true
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(data => {
        this.bookingForm.controls['consent'] = data;
      });
  }

  checkBaggage(e: MatCheckboxChange) {
    if (e.checked) {
      this.bookingForm.controls['onlyHandBaggage'].setValue(1);
    }
    if (!e.checked) {
      this.bookingForm.controls['onlyHandBaggage'].setValue(0);
    }

    if (this.bookingConfigShuttleArrival?.type == 'ARRIVALFLIGHTANDTIMETABLE') {
      this.selectArrivalChild?.resetForm();
      this.bookingForm.controls.routeArrival.setValue('');
      this.bookingForm.controls.terminalArrival.setValue('');
    }
  }

  resetDepartureArrivalFields() {
    this.departureTravelTime = null;
    this.arrivalTravelTime = null;
    this.plannedShuttleBussesDeparture = [];
    this.plannedShuttleBussesArrival = [];

    if (this.bookingForm) {
      this.bookingForm.patchValue({
        flightDepartureDate: '',
        routeDeparture: '',
        terminalDeparture: '',
        shuttleDepartureTime: '',
        flightArrivalDate: '',
        routeArrival: '',
        terminalArrival: ''
      });
    }

    this.selectDepartureChild?.resetForm();
    this.selectArrivalChild?.resetForm();
  }

  formatShuttleTime(date: any): string {
    return Intl.DateTimeFormat('sv-SE', {
      timeZone: this.getOrgTimeZone(),
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(date));
  }

  getShuttleTimeBeforeFlight() { }

  resetForm() {
    this.bookingForm.reset();
    if (this.selectDepartureChild) {
      this.selectDepartureChild.resetForm();
    }
    if (this.selectArrivalChild) {
      this.selectArrivalChild.resetForm();
    }
    this.arrivalTravelTime = null;
    this.departureTravelTime = null;
    this.plannedShuttleBussesArrival = [];
    this.plannedShuttleBussesDeparture = [];
    this.isBusOverbooked = false;
  }

  getOrgTimeZone(): string {
    const config = this.myConfigService.getMyConfig();
    const org = config?.organizations?.find(o => o.organizationId?.toLowerCase() === this.organizationId?.toLowerCase());
    const tz = org?.config?.timeZone;
    if (!tz) return 'Europe/Stockholm';
    const iana = findIana(tz);
    return iana?.[0] ?? 'Europe/Stockholm';
  }

  isArrivalFlightExpired(): boolean {
    if (!this.showArrival || !this.arrivalTravelTime?.flight?.arrivalTime?.scheduled) {
      return false;
    }
    const tz = this.getOrgTimeZone();
    const arrivalTime = moment.tz(this.arrivalTravelTime.flight.arrivalTime.scheduled, tz);
    return moment().tz(tz).isAfter(arrivalTime);
  }

  openBottomSheet() {
    this.arrivalBookingError = null;

    if (this.bookingConfigShuttleArrival?.type?.toUpperCase() === 'ARRIVALFLIGHTANDTIMETABLE' || this.bookingConfigShuttleArrival?.type?.toUpperCase() === 'ARRIVALFLIGHT') {
      if (this.isArrivalFlightExpired()) {
        this.resetArrivalFields();
        this.arrivalBookingError = this.translate.translate('ERROR.FLIGHT_ALREADY_ARRIVED');
        return;
      }
    }

    if (this.bookingConfigShuttleArrival?.type?.toUpperCase() === 'ARRIVALTIMETABLE') {
      const routeArrival = this.bookingForm.controls['routeArrival'].value;
      const tz = this.getOrgTimeZone();
      if (routeArrival?.startDate && moment().tz(tz).isAfter(moment(routeArrival.startDate))) {
        this.resetArrivalFields();
        this.arrivalBookingError = this.translate.translate('ERROR.TRANSFER_TIME_PASSED');
        return;
      }
    }

    let bookingData: Booking = INIT_DATA;
    bookingData.parking = null;
    bookingData.shuttles = [];

    bookingData.totalPrice = 0;
    bookingData.totalDiscountPrice = 0;
    bookingData.discountAmount = 0;
    bookingData.discountPercent = 0;
    bookingData.customer = this.getBookingCustomer();
    bookingData.order = this.getBookingOrder();

    if (this.hasShuttleDepartureConfig() && this.showDeparture) {
      bookingData.shuttles.push(this.getBookingShuttleDeparture());
      bookingData.order.productCode = this.bookingConfigShuttleDeparture.productCode;
    }

    if (this.hasShuttleArrivalConfig() && this.showArrival) {
      bookingData.shuttles.push(this.getBookingShuttleArrival());
      bookingData.order.productCode = this.bookingConfigShuttleArrival.productCode;
    }

    if (this.bookingConfigShuttleDeparture && this.bookingConfigShuttleArrival && this.showDeparture && this.showArrival) {
      bookingData.order.bundleCode = this.bookingConfigType?.orderConfig?.bundleCode || null;
      bookingData.order.productCode = null;
    }

    this.isLoading = true;
    this.cartService
      .addOrder(bookingData)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(cart => {

        if (cart && cart.totalPriceIncVat > 0) {
          const dialogRef = this.dialog.open(PaymentBottomsheetComponent, {
            width: '600px',
            data: {
              cart,
              customer: bookingData
            }
          });

          dialogRef
            .afterClosed()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => { });
        }

        if (cart && cart.totalPriceIncVat == 0) {

          this.cartService
            .setOrderStatusPaid(cart.orderId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {

              const isQuality = this.organizationId?.toUpperCase() === '39218780-069A-4C6B-9925-22365A075C15';
              const isAiden = this.organizationId?.toUpperCase() === 'D6B9D8C6-8FB5-41A5-AC2E-B3FC007A8251';

              if (this.bookingConfigType?.sendOrderConf) {
                this.cartService
                  .sendOrderConf(cart.orderId)
                  .pipe(takeUntil(this.componentDestroyed$))
                  .subscribe(() => {
                    if (isQuality && localStorage.getItem('quality-public') === 'true') {
                      this.router.navigate(['/parkifly/quality-landing-page'], { queryParams: { public: 'true' } });
                    } else if (isAiden && localStorage.getItem('aiden-public') === 'true') {
                      this.router.navigate(['/parkifly/aiden-landing-page'], { queryParams: { public: 'true' } });
                    } else {
                      this.router.navigate(['/flygets/checkout/receipt/', cart.orderId, 'HOTEL']);
                      localStorage.removeItem('quality-public');
                      localStorage.removeItem('aiden-public');
                    }
                  });
              } else {
                if (isQuality && localStorage.getItem('quality-public') === 'true') {
                  this.router.navigate(['/parkifly/quality-landing-page'], { queryParams: { public: 'true' } });
                } else if (isAiden && localStorage.getItem('aiden-public') === 'true') {
                  this.router.navigate(['/parkifly/aiden-landing-page'], { queryParams: { public: 'true' } });
                } else {
                  this.router.navigate(['/flygets/checkout/receipt/', cart.orderId, 'HOTEL']);
                  localStorage.removeItem('quality-public');
                  localStorage.removeItem('aiden-public');
                }
              }
            });
        }
      });
  }

  getBookingCustomer(): Customer {

    let customer: Customer = {
      firstname: this.bookingForm.controls['firstname'].value,
      lastname: this.bookingForm.controls['lastname'].value,
      email: this.bookingForm.controls['email'].value,
      cellular: this.bookingForm.controls['cellular'].value
    };

    return customer;
  }

  getBookingOrder(): OrderType {

    let order: OrderType = {
      organizationId: this.bookingConfig?.organizationId || '',
      bundleCode: null,
      productCode: null,
      discountCode: null,
      comment: this.getComment(),
      externalBookingReference: this.bookingForm.controls['bookingnr'].value
    };

    return order;
  }

  getBookingShuttleDeparture(): Shuttle {

    let baggage = 1;
    if (this.bookingForm.controls['onlyHandBaggage'].value === 1) {
      baggage = 0;
    }

    let shuttleDeparture: Shuttle = {
      stationcode: this.bookingConfigShuttleDeparture?.stationCode || '',
      shuttletypecode: this.bookingConfigShuttleDeparture?.shuttleTypeCode || '',
      productcode: this.bookingConfigShuttleDeparture?.productCode || '',
      shuttledate: null,
      flightnr: null,
      flightdate: null,
      flightterminal: null,
      terminal: null,
      passengers: this.bookingForm.controls['persons'].value,
      childseats: 0,
      baggage: baggage,
      comment: this.getComment(),
      ordershuttlebusid: null
    };

    if ((this.bookingConfigShuttleDeparture?.type)?.toUpperCase() === 'DEPARTUREFLIGHT') {
      shuttleDeparture.shuttledate = moment(this.departureTravelTime.flight.departureTime.scheduled).format("YYYY-MM-DDTHH:mm");
      shuttleDeparture.flightnr = this.departureTravelTime.flight.flightId;
      shuttleDeparture.flightdate = moment(this.departureTravelTime.flight.departureTime.scheduled).format();
      shuttleDeparture.flightdeparturedate = this.departureTravelTime.flight.flightLegIdentifier.flightDepartureDateUtc;
      shuttleDeparture.flightterminal = this.departureTravelTime.flight.locationAndStatus.terminal;
      shuttleDeparture.terminal = this.departureTravelTime.flight.locationAndStatus.terminal;
    }

    if ((this.bookingConfigShuttleDeparture?.type)?.toUpperCase() === 'DEPARTUREFLIGHTANDTIME') {
      shuttleDeparture.shuttledate = moment(this.bookingForm.controls['shuttleDepartureTime'].value).format("YYYY-MM-DDTHH:mm");
      shuttleDeparture.flightnr = this.departureTravelTime.flight.flightId;
      shuttleDeparture.flightdate = moment(this.departureTravelTime.flight.departureTime.scheduled).format();
      shuttleDeparture.flightdeparturedate = this.departureTravelTime.flight.flightLegIdentifier.flightDepartureDateUtc;
      shuttleDeparture.flightterminal = this.departureTravelTime.flight.locationAndStatus.terminal;
      shuttleDeparture.terminal = this.departureTravelTime.flight.locationAndStatus.terminal;
    }

    if ((this.bookingConfigShuttleDeparture?.type)?.toUpperCase() === 'DEPARTURETIMETABLE') {
      shuttleDeparture.shuttledate = moment(this.bookingForm.controls['flightDepartureDate'].value).format("YYYY-MM-DD") + 'T' + moment(this.bookingForm.controls['routeDeparture'].value.startDate).format("HH:mm");
      shuttleDeparture.terminal = this.bookingForm.controls['terminalDeparture'].value;
      shuttleDeparture.ordershuttlebusid = this.bookingForm.controls['routeDeparture'].value.orderShuttleBusId;
    }

    return shuttleDeparture;
  }

  getBookingShuttleArrival(): Shuttle {
    let baggage = 1;
    if (this.bookingForm.controls['onlyHandBaggage'].value === 1) {
      baggage = 0;
    }

    let shuttleArrival: Shuttle = {
      stationcode: this.bookingConfigShuttleArrival?.stationCode || '',
      shuttletypecode: this.bookingConfigShuttleArrival?.shuttleTypeCode || '',
      productcode: this.bookingConfigShuttleArrival?.productCode || '',
      shuttledate: null,
      flightnr: null,
      flightdate: null,
      flightterminal: null,
      terminal: null,
      passengers: this.bookingForm.controls['persons'].value,
      childseats: 0,
      baggage: baggage,
      comment: this.getComment(),
      ordershuttlebusid: null
    };

    if ((this.bookingConfigShuttleArrival?.type)?.toUpperCase() === 'ARRIVALFLIGHT') {
      shuttleArrival.shuttledate = moment(this.arrivalTravelTime.flight.arrivalTime.scheduled).add(this.bookingConfigShuttleArrival.shuttleDateOffsetMinutes, 'm').format("YYYY-MM-DDTHH:mm");
      if (baggage == 0) {
        shuttleArrival.shuttledate = moment(this.arrivalTravelTime.flight.arrivalTime.scheduled).add(this.bookingConfigShuttleArrival.shuttleDateOffsetMinutesNoBag, 'm').format("YYYY-MM-DDTHH:mm");
      }

      shuttleArrival.flightnr = this.arrivalTravelTime.flight.flightId;
      shuttleArrival.flightdate = moment(this.arrivalTravelTime.flight.arrivalTime.scheduled).format();
      shuttleArrival.flightdeparturedate = this.arrivalTravelTime.flight.flightLegIdentifier.flightDepartureDateUtc;
      shuttleArrival.flightterminal = this.arrivalTravelTime.flight.locationAndStatus.terminal;
      shuttleArrival.terminal = this.arrivalTravelTime.flight.locationAndStatus.terminal;
    }

    if ((this.bookingConfigShuttleArrival?.type)?.toUpperCase() === 'ARRIVALFLIGHTANDTIMETABLE') {
      shuttleArrival.shuttledate = moment(this.bookingForm.controls['routeArrival'].value.startDate).format("YYYY-MM-DDTHH:mm");
      shuttleArrival.flightnr = this.arrivalTravelTime.flight.flightId;
      shuttleArrival.flightdate = moment(this.arrivalTravelTime.flight.arrivalTime.scheduled).format();
      shuttleArrival.flightdeparturedate = this.arrivalTravelTime.flight.flightLegIdentifier.flightDepartureDateUtc;
      shuttleArrival.flightterminal = this.arrivalTravelTime.flight.locationAndStatus.terminal;
      shuttleArrival.terminal = this.arrivalTravelTime.flight.locationAndStatus.terminal;
      shuttleArrival.ordershuttlebusid = this.bookingForm.controls['routeArrival'].value.orderShuttleBusId;
    }

    if ((this.bookingConfigShuttleArrival?.type)?.toUpperCase() === 'ARRIVALTIMETABLE') {
      shuttleArrival.shuttledate = moment(this.bookingForm.controls['flightArrivalDate'].value).format("YYYY-MM-DD") + 'T' + moment(this.bookingForm.controls['routeArrival'].value.startDate).format("HH:mm");
      shuttleArrival.terminal = this.bookingForm.controls['terminalArrival'].value;
      shuttleArrival.ordershuttlebusid = this.bookingForm.controls['routeArrival'].value.orderShuttleBusId;
      this.showHandbaggage = false;
    }

    return shuttleArrival;
  }

  getComment(): string {

    let comment: string = '';

    if (this.bookingForm.controls['carid'].value != '') {
      comment = 'Regnr:' + this.bookingForm.controls['carid'].value;
    }

    if (this.bookingForm.controls['bookingnr'].value != '') {
      comment = comment + ' Bokningsnr:' + this.bookingForm.controls['bookingnr'].value;
    }

    if (this.bookingForm.controls['comment'].value != '') {
      comment = comment + ' Kommentar:' + this.bookingForm.controls['comment'].value;
    }

    return comment;
  }

  showDepartureChange($event) {
    this.showDeparture = $event.checked;
    this.intitializeForm();
  }

  showArrivalChange($event) {
    this.showArrival = $event.checked;
    this.intitializeForm();
  }

  showDepartureClick() {
    if (this.showDeparture) {
      this.showDeparture = false;
    } else {
      this.showDeparture = true;
    }
    this.intitializeForm();
  }

  showArrivalClick() {
    if (this.showArrival) {
      this.showArrival = false;
    } else {
      this.showArrival = true;
    }
    this.intitializeForm();
  }

  hasShuttleDepartureConfig() {
    return this.bookingConfigShuttleDeparture != null;
  }

  hasShuttleArrivalConfig() {
    return this.bookingConfigShuttleArrival != null;
  }

  getOrganizationConfig(organizationId: string) {
    const bookingConfigs: BookingConfig[] = [];

    bookingConfigs.push(this.bookingConfigQuality);
    bookingConfigs.push(this.bookingConfigScandic);
    bookingConfigs.push(this.bookingConfigMabi);
    bookingConfigs.push(this.bookingConfigArlandaXPOApartments);
    bookingConfigs.push(this.bookingConfigAppart);
    bookingConfigs.push(this.bookingConfigAiden);

    this.bookingConfig = bookingConfigs.find(f => (f.organizationId)?.toUpperCase() === (organizationId)?.toUpperCase());
    this.bookingConfigType = this.bookingConfig.bookingConfigTypes.find(f => (f.configType)?.toUpperCase() === 'HOTEL');

    this.updateShuttleConfigs();
    this.intitializeForm();
  }

  updateShuttleConfigs() {
    this.bookingConfigType = this.bookingConfig?.bookingConfigTypes?.find(f => (f.configType)?.toUpperCase() === 'HOTEL');

    if (!this.bookingConfigType) {
      console.error('BookingConfigType not found for HOTEL');
      return;
    }

    this.bookingConfigShuttleDeparture = this.bookingConfigType.shuttlesConfig?.find(f => f.type?.startsWith('DEPARTURE')) || null;

    if (!this.useFlightForArrival) {
      const directConfig = this.bookingConfig?.bookingConfigTypes?.find(f => (f.configType)?.toUpperCase() === 'DIRECT');
      this.bookingConfigShuttleArrival = directConfig?.shuttlesConfig?.find(f => f.type?.startsWith('ARRIVAL')) || null;
    } else {
      this.bookingConfigShuttleArrival = this.bookingConfigType.shuttlesConfig?.find(f => f.type?.startsWith('ARRIVAL')) || null;
    }
  }

  onArrivalModeToggle() {
    this.resetArrivalFields();
    this.updateShuttleConfigs();
  }

  resetArrivalFields() {
    if (this.bookingForm) {
      this.bookingForm.controls['flightArrivalDate']?.setValue('');
      this.bookingForm.controls['routeArrival']?.setValue('');
      this.bookingForm.controls['terminalArrival']?.setValue('');

      if (this.selectArrivalChild) {
        this.selectArrivalChild.resetForm();
      }
    }

    this.arrivalTravelTime = null;
    this.plannedShuttleBussesArrival = [];
    this.arrivalBookingError = null;
  }

  clearArrivalBookingError() {
    this.arrivalBookingError = null;
  }

  resetFormFields() {
    if (this.bookingForm) {
      this.bookingForm.controls['flightArrivalDate']?.setValue('');
      this.bookingForm.controls['routeArrival']?.setValue('');
      this.bookingForm.controls['terminalArrival']?.setValue('');

      if (this.selectArrivalChild) {
        this.selectArrivalChild.resetForm();
      }
    }

    this.arrivalTravelTime = null;
    this.plannedShuttleBussesArrival = [];
  }

  dateChangedDeparture(event: MatDatepickerInputEvent<Date>) {
    this.plannedShuttleBussesDeparture = [];
    if (!event.value) return;
    const selectedDate: Moment = moment(event.value);
    if (!selectedDate.isValid()) return;
    const selectedStartDate = selectedDate.format('YYYY-MM-DD');
    const endDate = selectedDate.clone().add(1, 'd').format('YYYY-MM-DD');

    if (this.bookingConfigShuttleDeparture?.routeCode && this.bookingConfigShuttleDeparture?.shuttleTypeCode) {
      this.shuttleBussService
        .getPlannedOrderShuttleBusType(this.bookingConfigShuttleDeparture.routeCode, selectedStartDate, endDate, this.bookingConfigShuttleDeparture.shuttleTypeCode)
        .subscribe(plannedShuttleBusses => {
          if (plannedShuttleBusses?.message) {
            this.departureDateError = plannedShuttleBusses.message;
          } else if (Array.isArray(plannedShuttleBusses)) {
            plannedShuttleBusses.forEach(shuttle => {
              shuttle.startDate = moment.tz(shuttle.startDate, this.getOrgTimeZone());
            });
            plannedShuttleBusses.filter(shuttle => {
              if (shuttle.startDate.isAfter(moment().tz(this.getOrgTimeZone()).add(20, 'm')) && (shuttle.seats == null || (shuttle.seats - shuttle.passengers >= this.bookingForm.controls['persons'].value))) {
                this.plannedShuttleBussesDeparture.push(shuttle);
              }
            });
            this.departureDateError = null;
          }
        });
    }
  }

  dateChangedArrival(event: MatDatepickerInputEvent<Date>) {
    this.plannedShuttleBussesArrival = [];
    if (!event.value) return;
    const selectedDate: Moment = moment(event.value);
    if (!selectedDate.isValid()) return;
    const selectedStartDate = selectedDate.format('YYYY-MM-DD');
    const endDate = selectedDate.clone().add(1, 'd').format('YYYY-MM-DD');
    const selectedEndDate = moment(endDate).format('YYYY-MM-DD');

    this.getPlannedOrderShuttleBusTypeArrival(selectedStartDate, selectedEndDate);
  }

  getPlannedOrderShuttleBusTypeArrival(startDate: string, endDate: string) {
    if (!startDate || !endDate || startDate.includes('NaN') || endDate.includes('NaN') || startDate.includes('Invalid') || endDate.includes('Invalid') || !moment(startDate).isValid() || !moment(endDate).isValid()) {
      return;
    }

    this.plannedShuttleBussesArrival = [];
    if (this.bookingConfigShuttleArrival?.routeCode && this.bookingConfigShuttleArrival?.shuttleTypeCode) {
      this.shuttleBussService
        .getPlannedOrderShuttleBusType(this.bookingConfigShuttleArrival.routeCode, startDate, endDate, this.bookingConfigShuttleArrival.shuttleTypeCode)
        .subscribe(plannedShuttleBusses => {
          if (plannedShuttleBusses?.message) {
            this.arrivalDateError = plannedShuttleBusses.message;
          } else if (Array.isArray(plannedShuttleBusses)) {
            plannedShuttleBusses.forEach(shuttle => {
              shuttle.startDate = moment.tz(shuttle.startDate, this.getOrgTimeZone());
            });
            plannedShuttleBusses.filter(shuttle => {
              if (shuttle.startDate.isAfter(moment().tz(this.getOrgTimeZone()).add(20, 'm')) && (shuttle.seats == null || (shuttle.seats - shuttle.passengers >= this.bookingForm.controls['persons'].value))) {
                this.plannedShuttleBussesArrival.push(shuttle);
              }
            });
            this.arrivalDateError = null;

            if (this.plannedShuttleBussesArrival.length < 3) {
              const endMom = moment(endDate);
              const startMom = moment(startDate);
              if (endMom.isValid() && startMom.isValid() && endMom.diff(startMom, 'hours') < 24) {
                const newEndDate = endMom.add(2, 'hours').format('YYYY-MM-DDTHH:mm:ss');
                this.getPlannedOrderShuttleBusTypeArrival(startDate, newEndDate);
              }
            }
          }
        });
    }
  }

  checkPersonsOnRoutes() {
    let availableSeats: number = 0;
    this.isBusOverbooked = false;

    if (this.bookingForm.controls['persons'].value) {
      if (this.bookingForm.controls['routeDeparture'].value) {
        availableSeats = this.bookingForm.controls['routeDeparture'].value.seats - this.bookingForm.controls['routeDeparture'].value.passengers - this.bookingForm.controls['persons'].value;
        if (availableSeats < 0) {
          this.isBusOverbooked = true;
        }
      }

      if (this.bookingForm.controls['routeArrival'].value) {
        availableSeats = this.bookingForm.controls['routeArrival'].value.seats - this.bookingForm.controls['routeArrival'].value.passengers - this.bookingForm.controls['persons'].value;
        if (availableSeats < 0) {
          this.isBusOverbooked = true;
        }
      }
    }
  }

  disableBookingForm(): boolean {

    let disableBookingForm = false;

    if (this.hasShuttleDepartureConfig() && this.showDeparture) {

      if ((this.bookingConfigShuttleDeparture?.type)?.toUpperCase() === 'DEPARTURETIMETABLE') {
        if (this.bookingForm.controls['flightDepartureDate'].value == '' || this.bookingForm.controls['routeDeparture'].value.startDate == null || this.bookingForm.controls['terminalDeparture'].value == '') {
          disableBookingForm = true;
        }
      }

      if ((this.bookingConfigShuttleDeparture?.type)?.toUpperCase() === 'DEPARTUREFLIGHT') {
        if (this.departureTravelTime == null) {
          disableBookingForm = true;
        }
      }

      if ((this.bookingConfigShuttleDeparture?.type)?.toUpperCase() === 'DEPARTUREFLIGHTANDTIME') {
        if (this.departureTravelTime == null || this.bookingForm.controls['shuttleDepartureTime'].value == '') {
          disableBookingForm = true;
        }
      }
    }

    if (this.hasShuttleArrivalConfig() && this.showArrival) {

      if ((this.bookingConfigShuttleArrival?.type)?.toUpperCase() === 'ARRIVALTIMETABLE') {
        if (this.bookingForm.controls['flightArrivalDate'].value == '' || this.bookingForm.controls['routeArrival'].value.startDate == null || this.bookingForm.controls['terminalArrival'].value == '') {
          disableBookingForm = true;
        }
      }

      if ((this.bookingConfigShuttleArrival?.type)?.toUpperCase() === 'ARRIVALFLIGHTANDTIMETABLE') {
        if (this.arrivalTravelTime == null || this.bookingForm.controls['routeArrival'].value.startDate == null) {
          disableBookingForm = true;
        }
      }

      if ((this.bookingConfigShuttleArrival?.type)?.toUpperCase() === 'ARRIVALFLIGHT') {
        if (this.arrivalTravelTime == null) {
          disableBookingForm = true;
        }
      }
    }

    if (this.isBusOverbooked) {
      disableBookingForm = true;
    }

    return disableBookingForm;
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
